import { matchPath, useHistory } from 'react-router-dom';
import { usePrevious } from 'ahooks';
import { ONBOARDING_STAGES, useWhen } from '@monument/core';
import { History } from 'history';
import { hasActiveOnboarding, useOnboarding } from '@services/onboarding';
import { parse } from 'query-string';

const isOnboardingRoute = pathname => matchPath(pathname, { path: '/onboarding' });

const SKIP_INITIAL_ONBOARDING_REDIRECT_FOR_PATHNAMES = [
  '/settings/insurance',
  '/insurance/card-images',
];

// One-time page-load procedure for routing based on member's onboarding status
export const validatePageLoadOnboardingRules = ({
  history,
  onboarding,
  transition,
}: {
  history: History<unknown>;
  onboarding: any;
  transition: any;
}) => {
  const { pathname } = history.location;
  const isSkipRedirectToOnboardingForPathname =
    SKIP_INITIAL_ONBOARDING_REDIRECT_FOR_PATHNAMES.includes(pathname);

  if (isSkipRedirectToOnboardingForPathname) {
    // If we don't redirect to an onboarding path, when the stage requires it, we have to exit
    // to make sure resume actions work as expected on skipped pages and after in-app navigation
    if (onboarding.stage !== ONBOARDING_STAGES.EXITED) {
      transition('exit');
    }
    return;
  }

  if (hasActiveOnboarding(onboarding) && !isOnboardingRoute(pathname)) {
    history.replace('/onboarding');
    return;
  }

  if (!hasActiveOnboarding(onboarding) && isOnboardingRoute(pathname)) {
    return history.replace('/dashboard');
  }
};

// After submitting payment, members may close onboarding client-side but the exit transition is
// a no-op on the server (so that subsequent page-loads redirect back to finish onboarding).
// They are allowed to exit after they have made an appointment
// and matched with a physician (paidActivated)
const isExitFromStagePersisted = onboarding => {
  return onboarding.store.paidActivated;
};

const getIsInvalidOnboardingNavigation = ({ onboarding, prevPathname, nextPathname }) => {
  if (!prevPathname || !onboarding) {
    return false;
  }
  return {
    isInvalidExit:
      isOnboardingRoute(prevPathname) &&
      !isOnboardingRoute(nextPathname) &&
      hasActiveOnboarding(onboarding) &&
      isExitFromStagePersisted(onboarding),
    isInvalidEntrance:
      !isOnboardingRoute(prevPathname) &&
      isOnboardingRoute(nextPathname) &&
      !hasActiveOnboarding(onboarding),
  };
};

/**
 * Patient onboarding uses a backend state machine to navigate through onboarding, persisting the
 * member's current stage to the DB. This approach presents some tricky challenges given that the
 * client is a web browser with its own state machine for tracking navigation (browser history).
 * Specifically, there's no reliable way to control what happens when a user clicks their browser's
 * back button.
 *
 * This hook provides a workaround by monitoring all changes to history.location.pathname and
 * allowing us to perform some (async) action (like redirecting) before allowing children to render
 * the new route that the browser has navigated to.
 *
 * When navigating between onboarding and non-onboarding routes, we compare the target route (and
 * the onboarding stage it corresponds to) with the backend's current state machine stage
 * (onboarding.stage) and resolve conflicts as needed.
 */
export const useInterceptInvalidOnboardingNavigation = () => {
  const history = useHistory();
  const { onboarding, transition } = useOnboarding();
  const nextPathname = history.location.pathname;
  // The callback () => true is saying we always want to update prevPathname;
  // without it prevPathName would only change if nextPathname was a different
  // value than prevPathname. Changing less frequently would create situations where
  // pathnames were erroneous:
  // - member is actually at /dashboard, but
  // - prevPathname is /onboarding
  // - nextPathname is /dashboard
  // which leads to isInvalidExit being true more often than it should,
  // which triggers erroneous transition('exit') calls.
  // see: https://ahooks-v2.js.org/hooks/state/use-previous
  // @BrandLibel
  const prevPathname = usePrevious(nextPathname, () => true);
  const { isInvalidEntrance, isInvalidExit } = getIsInvalidOnboardingNavigation({
    prevPathname,
    nextPathname,
    onboarding,
  });

  // From not onboarding -> onboarding + no active onboarding: back to not onboarding
  // @TODO: consider scenarios where we want to allow browser back button to go back to onboarding
  // and can derive the necessary transition.
  useWhen(isInvalidEntrance, () => {
    history.push(prevPathname);
  });

  // From onboarding -> not onboarding + has active onboarding + this transition requires exiting:
  // try to exit (and go back to onboarding if that fails)
  useWhen(isInvalidExit, () => {
    transition('exit').catch(() => history.push(prevPathname));
  });

  return isInvalidEntrance || isInvalidExit;
};

// For query params that trigger a one-time action and then get deleted.
export const processQueryParams = ({ history }: { history: History<unknown> }) => {
  const { location } = history;
  const query = parse(location.search);
  if (query.trackSignUp) {
    history.replace(location.pathname);
  }
};
